'use client';
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { captureException } from '@/lib/sentry';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  eventId: string | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, eventId: null };
  }

  static getDerivedStateFromError(_: Error): Partial<State> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Report to Sentry (only works in production)
    const eventId = captureException(error, {
      errorBoundary: 'ErrorBoundary',
      extra: {
        componentStack: errorInfo.componentStack,
      },
    });

    this.setState({ eventId });
  }

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center h-screen gap-4">
          <h1 className="text-2xl font-bold mb-4">Something went wrong.</h1>
          <p className="text-gray-600 mb-4 text-center max-w-md">
            We&apos;ve been notified of this error and are working to fix it.
          </p>
          {this.state.eventId && (
            <p className="text-sm text-gray-500 mb-4">
              Error ID:{' '}
              <code className="bg-gray-100 px-2 py-1 rounded text-xs">
                {this.state.eventId}
              </code>
            </p>
          )}
          <div className="flex gap-4">
            <button
              onClick={this.handleReload}
              className="rounded-xl bg-blue-500 text-white px-6 py-3 font-semibold hover:bg-blue-600 transition-colors"
            >
              Try Again
            </button>
            <button
              onClick={this.handleGoHome}
              className="rounded-xl bg-gray-200 px-6 py-3 font-semibold hover:bg-gray-300 transition-colors"
            >
              Go to Home
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
